import { Component, OnInit, OnDestroy, Inject, Input } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { BehaviorSubject, combineLatest, filter, map, Observable, of, Subscription } from 'rxjs';

import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { TopicsService } from '../../../Core/services/topics.service';
import { FormControl, FormGroup, FormGroupDirective, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { MonitoringSeriesTypeEnum } from '@Monitor/Shared/enums/monitoring-series-type.enum';
import { TagsService } from '@Common/Tags/services/tags.service';
import { FolderService } from '@Monitor/Core/services/folder.service';
import { Folder } from '@Monitor/Shared/models/folder.model';
import { TagModel } from '@Common/Tags/models/tag.model';
import { DeliveryRangeModel } from '@Common/Forms/models/delivery-range.model';
import { PreferencesModel } from '@Core/Users/models/preferences.model';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { MonitorConfigService } from '@Common/MonitorShared/services/monitor-config.service';
import moment, { Moment } from 'moment-timezone';
import { AutocompleteCustomOption, AppearCondition } from '../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { MentionSavingQuota } from '../../../../api/monitoring/model/monitoring-account-configuration-api.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { MentionVolumeEstimateDialogComponent } from '../MentionVolumeEstimateDialog/mention-volume-estimate-dialog.component';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';

export interface TopicWorkflowFormGroup {
    autoTag: FormGroup<AutoTagGroup>;
    autoSave: FormGroup<AutoSaveGroup>;
}

export interface AutoTagGroup {
    isAutoTaggingSearch: FormControl<boolean>;
    tagIds: FormControl<number[]>;
}

export interface AutoSaveGroup {
    saveEnabled: FormControl<boolean>;
    startDate: FormControl<Moment>;
    endDate: FormControl<Moment>;
    tagIds: FormControl<number[]>;
    folderIds: FormControl<number[]>;
    timezone: FormControl<string>;
}

interface Chip {
    id: number;
    type: string;
    displayString: string;
}

@Component({
    selector: 'agpr-topic-workflow-settings',
    templateUrl: './topic-workflow-settings.component.html',
    styleUrls: ['./topic-workflow-settings.component.scss'],
    standalone: false
})
export class TopicWorkflowSettingsComponent implements OnInit, OnDestroy {
    id = 'topicWorkFlowSettings';
    @Input() formGroupName: string;
    @Input() showErrors: boolean = false;
    overlayContainer: Element;
    form: FormGroup;

    // Auto-Tagging
    maxNumberOfAutoTaggingTopics: number;
    maxNumberOfTags: number;
    accountAutoTaggingMaxReached: boolean;
    useTopicToTag: boolean = false;

    // Auto-Saving
    useAutoSave: boolean = false;
    isExistingAutoSave: boolean = false; // The initial value of "useAutoSave", which indicates if it’s an existing auto-saving topic.
    autoSavingProvisioned: boolean;
    accountAutoSavingMaxReached: boolean;
    maxNumberOfAutoSavingTopics: number;
    autoSavingOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('Monitoring.searchCriteria.folders', MonitoringSeriesTypeEnum.folder),
        new DropdownOptionModel('Monitoring.searchCriteria.tags', MonitoringSeriesTypeEnum.tag),
        new DropdownOptionModel('Monitoring.searchCriteria.allSavedCoverage', MonitoringSeriesTypeEnum.allSavedCoverage)
    ];
    selectedAutoSavingSourceOption: DropdownOptionModel;
    autoSavingSource$: Observable<any[]> = of(null);
    selectedAutoSavingSources: any[] = [];
    public autoSavingSourceTypeToStringMap = {
        [MonitoringSeriesTypeEnum.folder]: '(FOL)',
        [MonitoringSeriesTypeEnum.tag]: '(TAG)'
    };

    autocompleteCustomOptions: AutocompleteCustomOption[] = [];
    foldersAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'Mention.manageMentionOrganization.createFolder',
            action: (folderName: string) => {
                const folder = new Folder();
                folder.name = folderName;

                this.foldersService.create(folder).pipe(
                    map(id => this.folders.find(itm => itm.id === id)),
                    filter(x => !!x)
                ).subscribe(newFolder => {
                    this.selectedAutoSavingSources.push(newFolder);
                    this.addAutoSavingChip(newFolder);
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    tagsAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'Mention.manageMentionOrganization.createTag',
            action: (tagName: string) => {
                const tag = new TagModel();
                tag.name = tagName;

                this.tagsService.createTag(tag).pipe(
                    map(id => this.tags.find(itm => itm.id === id)),
                    filter(x => !!x)
                ).subscribe(newTag => {
                    this.selectedAutoSavingSources.push(newTag);
                    this.addAutoSavingChip(newTag);
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];
    folders: Folder[] = [];
    tags: TagModel[] = [];
    chips: Chip[] = [];
    autoSavingTimeRange: DeliveryRangeModel = new DeliveryRangeModel();
    minEndDate = new Date();

    quotaInfo: MentionSavingQuota;

    private subscriptions: Subscription[] = [];
    preferences: PreferencesModel;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly monitoringSeriesTypes = MonitoringSeriesTypeEnum;
    readonly buttonTypes = ButtonTypesEnum;

    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private topicsService: TopicsService,
        private rootFormGroup: FormGroupDirective,
        private tagsService: TagsService,
        private foldersService: FolderService,
        private currentUserStateService: CurrentUserStateService,
        private monitorConfigService: MonitorConfigService,
        private coreDialogService: CoreDialogService
    ) {
    }


    ngOnInit() {
        this.overlayContainer = document.getElementById('main-content');
        this.form = this.rootFormGroup.control.get(this.formGroupName) as FormGroup<TopicWorkflowFormGroup>;
        this.form.addValidators([
            this.autoTaggingRequiredValidator(),
            this.autoSavingSourceRequiredValidator(),
            this.autoSavingDateRequiredValidator(),
            this.autoSavingDateRangeValidator()
        ]);

        this.maxNumberOfAutoTaggingTopics = this.topicsService.maxTaggingTopics;
        this.maxNumberOfTags = this.topicsService.numberOfTagsPerTopic;
        this.maxNumberOfAutoSavingTopics = this.topicsService.maxAutoSavingTopics;

        if (this.tagIds.value?.length > 0) {
            this.useTopicToTag = true;
        } else {
            this.useTopicToTag = false;
        }
        this.subscriptions.push(
            this.topicsService.getTaggingTopicsLimitReached().subscribe(
                (autoTaggingLimitReached) => {
                    this.accountAutoTaggingMaxReached = autoTaggingLimitReached;
                }
            ),
            this.topicsService.getAutoSavingTopicsLimitReached().subscribe(
                (autoSavingLimitReached) => {
                    this.accountAutoSavingMaxReached = autoSavingLimitReached;
                }
            ),
            this.tagIds.valueChanges.subscribe(tagIds => {
                if (tagIds?.length > 0) {
                    this.useTopicToTag = true;
                }
            }),
            this.isAutoTaggingSearch.valueChanges.subscribe(isAutoTaggingSearch => {
                // use patch value here to cause radio component to write value and update checked state
                this.isAutoTaggingSearch.patchValue(isAutoTaggingSearch, { emitEvent: false });
            }),
            this.enableAutoSaving.valueChanges.subscribe(enabled => {
                if (enabled) {
                    this.selectedAutoSavingSourceOption = this.autoSavingOptions.find(x => x.value === MonitoringSeriesTypeEnum.folder);
                    if (this.autoSavingTagIds.value.length === 0 && this.autoSavingFolderIds.value.length === 0) {
                        this.selectedAutoSavingSourceOption = this.autoSavingOptions.find(x => x.value === MonitoringSeriesTypeEnum.allSavedCoverage);
                    }
                    this.selectAutoSavingSource(this.selectedAutoSavingSourceOption.value);
                }
            }),
            combineLatest([
                this.foldersService.all$,
                this.tagsService.all$
            ]).subscribe(([folders, tags]) => {
                this.folders = folders;
                this.tags = tags;
                this.setSelectedAutoSavingSources();
            }),

            this.currentUserStateService.getPreferences().subscribe(
                preferences => {
                    this.preferences = preferences;
                    this.setAutoSavingSettings();
                }
            ),
            this.monitorConfigService.getAutoSavingTopicLimit().subscribe(autoSaveTopicLimit => {
                this.autoSavingProvisioned = !!autoSaveTopicLimit;
            }),
            this.topicsService.getFocusedTopic().pipe(filter(x => !!x)).subscribe(() => this.initAutoSaving()),
            this.monitorConfigService.getMentionSavingQuota().subscribe(quotaInfo => {
                this.quotaInfo = quotaInfo;
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    initAutoSaving() {
        this.setSelectedAutoSavingSources();
        this.setAutoSavingSettings(true);
    }

    setSelectedAutoSavingSources() {
        const autoSavingFolders: Folder[] = this.autoSavingFolderIds.value?.map(id => this.folders.find(folder => folder.id === id)) || [];
        const autoSavingTags: TagModel[] = this.autoSavingTagIds.value?.map(id => this.tags.find(tag => tag.id === id)) || [];
        this.selectedAutoSavingSources = [...autoSavingFolders, ...autoSavingTags];
        this.chips = [];
        this.selectedAutoSavingSources.forEach(source => this.addAutoSavingChip(source));
    }

    setAutoSavingSettings(isInit: boolean = false) {
        if (!this.autoSaveGroup) {
            return;
        }
        const timezone = this.timezone?.value ? this.timezone.value : this.preferences?.timeZone ? this.preferences.timeZone : 'utc';
        this.autoSavingTimeRange = {
            ongoing: !this.autoSavingEndDate.value?.isValid(),
            start: moment(this.autoSavingStartDate?.value)?.tz(timezone)?.toDate(),
            end: moment(this.autoSavingEndDate?.value)?.tz(timezone)?.toDate(),
            timezone: timezone
        };
        this.useAutoSave = this.enableAutoSaving?.value;
        if (isInit) {
            this.isExistingAutoSave = this.useAutoSave;
        }
        this.selectedAutoSavingSourceOption = this.autoSavingOptions.find(x => x.value === MonitoringSeriesTypeEnum.folder);
        if (this.autoSavingTagIds.value?.length === 0 && this.autoSavingFolderIds.value?.length === 0) {
            this.selectedAutoSavingSourceOption = this.autoSavingOptions.find(x => x.value === MonitoringSeriesTypeEnum.allSavedCoverage);
        }
        this.selectAutoSavingSource(this.selectedAutoSavingSourceOption.value);
    }

    get autoTagGroup(): FormGroup {
        return this.form.get('autoTag') as FormGroup;
    }
    get autoSaveGroup(): FormGroup {
        return this.form.get('autoSave') as FormGroup;
    }

    // auto tagging
    get isAutoTaggingSearch(): FormControl {
        return this.autoTagGroup.get('isAutoTaggingSearch') as FormControl;
    }
    get tagIds(): FormControl {
        return this.autoTagGroup.get('tagIds') as FormControl;
    }

    // auto saving
    get enableAutoSaving(): FormControl {
        return this.autoSaveGroup.get('saveEnabled') as FormControl;
    }
    get autoSavingTagIds(): FormControl {
        return this.autoSaveGroup.get('tagIds') as FormControl;
    }
    get autoSavingFolderIds(): FormControl {
        return this.autoSaveGroup.get('folderIds') as FormControl;
    }
    get autoSavingStartDate(): FormControl {
        return this.autoSaveGroup.get('startDate') as FormControl;
    }
    get autoSavingEndDate(): FormControl {
        return this.autoSaveGroup.get('endDate') as FormControl;
    }
    get timezone(): FormControl {
        return this.autoSaveGroup.get('timezone') as FormControl;
    }

    toggleAutoTagging() {
        if (this.useTopicToTag) {
            this.isAutoTaggingSearch.setValue(true);
        } else {
            this.tagIds.setValue([]);
            this.isAutoTaggingSearch.setValue(null);
            this.accountAutoTaggingMaxReached = false;
        }
    }

    toggleAutoSaving() {
        this.enableAutoSaving.setValue(this.useAutoSave);
    }

    /**
     * Checks to see if the tags id is provided when use topic to tag mentions
     * @returns an error object with the key 'tagRequiredError' when invalid, otherwise return null
     */
    private autoTaggingRequiredValidator(): ValidatorFn {
        return (group: FormGroup<TopicWorkflowFormGroup>): ValidationErrors | null => {
            const tagIds = group.get('autoTag.tagIds').value;
            const tagRequired = this.useTopicToTag && (!tagIds || tagIds?.length === 0);
            return tagRequired ? { tagRequiredError: true } : null;
        };
    }

    private autoSavingSourceRequiredValidator(): ValidatorFn {
        return (group: FormGroup<TopicWorkflowFormGroup>): ValidationErrors | null => {
            const tagIds = group.get('autoSave.tagIds').value;
            const folderIds = group.get('autoSave.folderIds').value;
            const required = group.get('autoSave.saveEnabled').value
                && (!tagIds || tagIds?.length === 0)
                && (!folderIds || folderIds?.length === 0)
                && this.selectedAutoSavingSourceOption.value !== MonitoringSeriesTypeEnum.allSavedCoverage
            ;
            return required ? { autoSavingSourceRequiredError: true } : null;
        };
    }

    private autoSavingDateRequiredValidator(): ValidatorFn {
        return (group: FormGroup<TopicWorkflowFormGroup>): ValidationErrors | null => {
            const endDate = group.get('autoSave.endDate').value;
            const required = group.get('autoSave.saveEnabled').value && this.autoSavingTimeRange?.ongoing === false && !endDate?.isValid();
            return required ? { autoSavingDateRequiredError: true } : null;
        };
    }
    private autoSavingDateRangeValidator(): ValidatorFn {
        return (group: FormGroup<TopicWorkflowFormGroup>): ValidationErrors | null => {
            if (group.get('autoSave.saveEnabled').value && this.autoSavingTimeRange?.ongoing === false) {
                const startDate = group.get('autoSave.startDate').value;
                const endDate = group.get('autoSave.endDate').value;
                return startDate?.isValid() && endDate?.isValid() && startDate?.isAfter(endDate) ? { autoSavingDateRangeError: true } : null;
            }
        };
    }

    selectAutoSavingSource(source: MonitoringSeriesTypeEnum) {
        if (source === MonitoringSeriesTypeEnum.folder) {
            this.autoSavingSource$ = this.foldersService.all$;
            this.autocompleteCustomOptions = this.foldersAutocompleteOptions;
        }
        if (source === MonitoringSeriesTypeEnum.tag) {
            this.autoSavingSource$ = this.tagsService.all$;
            this.autocompleteCustomOptions = this.tagsAutocompleteOptions;
        }
        if (source === MonitoringSeriesTypeEnum.allSavedCoverage) {
            this.autoSavingSource$ = of([]);
            this.autocompleteCustomOptions = [];
            this.chips.forEach(chip => this.removeChip(chip));
            if (this.selectedAutoSavingSourceOption.value === MonitoringSeriesTypeEnum.allSavedCoverage) {
                this.autoSavingFolderIds.setValue([]);
                this.autoSavingTagIds.setValue([]);
            }
        }
        this.form.updateValueAndValidity();
    }

    selectedAutoSavingSourcesChanged(sources) {
        this.selectedAutoSavingSources = sources;

        const tagIds = [];
        const folderIds = [];
        sources.forEach(source => {
            if (source instanceof Folder) {
                folderIds.push(source.id);
            }
            if (source instanceof TagModel) {
                tagIds.push(source.id);
            }
        });

        this.autoSavingFolderIds.setValue(folderIds);
        this.autoSavingTagIds.setValue(tagIds);
    }

    addAutoSavingChip(source) {
        const type = source instanceof Folder ? MonitoringSeriesTypeEnum.folder : MonitoringSeriesTypeEnum.tag;
        const newChip: Chip = {
            id: source.id,
            type: type,
            displayString: source.name
        };
        this.chips.push(newChip);
    }

    removeChip(chip: Chip) {
        this.chips = this.chips.filter(x => x !== chip);
        this.selectedAutoSavingSources.splice(this.selectedAutoSavingSources.findIndex((source) => {
            if (chip.type === MonitoringSeriesTypeEnum.folder) {
                return source.id === chip.id && source instanceof Folder;
            }

            if (chip.type === MonitoringSeriesTypeEnum.folder) {
                return source.id === chip.id && source instanceof TagModel;
            }
        }), 1);
        this.selectedAutoSavingSourcesChanged(this.selectedAutoSavingSources);
    }

    autoSavingTimeRangeChanged(value: DeliveryRangeModel) {
        if (!value) {
            this.autoSavingTimeRange.ongoing = true;
            this.autoSavingTimeRange.timezone = this.preferences?.timeZone;
        } else {
            this.autoSavingTimeRange.ongoing = value.ongoing;
            this.autoSavingTimeRange.start = !this.autoSavingTimeRange.ongoing ? value.start : null;
            this.autoSavingTimeRange.end = !this.autoSavingTimeRange.ongoing ? value.end : null;
            this.autoSavingTimeRange.timezone = value.timezone;
        }
        this.autoSavingStartDate.setValue(moment(this.autoSavingTimeRange?.start).tz(this.autoSavingTimeRange.timezone));
        this.autoSavingEndDate.setValue(moment(this.autoSavingTimeRange?.end).tz(this.autoSavingTimeRange.timezone));
        this.timezone.setValue(this.autoSavingTimeRange.timezone);
    }

    openUsageEstimateDialog(): void {
        this.coreDialogService.open(MentionVolumeEstimateDialogComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                )
            }
        });
    }
}
